import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { systemLabelKey } from "../../../catalogs/characterSystemCatalog";
import { getLocalizedInventoryItemName } from "../../../catalogs/inventoryCatalog";
import { useDismissiblePopover } from "../../../hooks/useDismissiblePopover";

import {
  calculateArmorClass,
  getArmorItems,
} from "../../../calculations/armorClass";

const PRIMARY_ARMOR_SLOTS = ["helmet", "armor", "shield"];

const formatModifier = (value) => (value > 0 ? `+${value}` : value);

const Armor = ({ character, onChange }) => {
  const { i18n, t } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language) === "en" ? "en" : "ru";
  const getItemName = (item) => getLocalizedInventoryItemName(item, language);
  const [openPicker, setOpenPicker] = useState(null);
  const { popoverRef, triggerRef } = useDismissiblePopover(
    Boolean(openPicker),
    () => setOpenPicker(null)
  );
  const armorItems = getArmorItems(character.inventory?.items);
  const accessories = armorItems.filter((item) => item.armorType === "other");
  const equippedAccessories = accessories.filter((item) => item.isActive);
  const characteristics = character.characteristics;

  const updateArmor = ({
    items = character.inventory.items,
    characteristicChanges = {},
  }) => {
    const updatedCharacteristics = {
      ...characteristics,
      ...characteristicChanges,
    };
    updatedCharacteristics.armorClass = calculateArmorClass(
      updatedCharacteristics,
      items
    );

    onChange({
      ...character,
      inventory: { ...character.inventory, items },
      characteristics: updatedCharacteristics,
    });
  };

  const selectPrimaryArmor = (armorType, selectedItemId) => {
    const items = character.inventory.items.map((item) => {
      if (
        item.type?.toLowerCase() === "armor" &&
        item.armorType === armorType
      ) {
        return {
          ...item,
          isActive: selectedItemId !== "" && item.id === selectedItemId,
        };
      }

      return item;
    });

    setOpenPicker(null);
    updateArmor({ items });
  };

  const toggleAccessory = (selectedItem) => {
    const items = character.inventory.items.map((item) =>
      item.id === selectedItem.id
        ? { ...item, isActive: !item.isActive }
        : item
    );

    updateArmor({ items });
  };

  const updateNumber = (key, value) => {
    updateArmor({ characteristicChanges: { [key]: Number(value) || 0 } });
  };

  const renderPrimaryPicker = (type, label, slotItems, equippedItem) => (
    <div className="armor-picker-wrapper">
      <button
        aria-expanded={openPicker === type}
        aria-label={t("equipment.choose", { name: label })}
        className="armor-picker-button"
        type="button"
        onClick={(event) => {
          triggerRef.current = event.currentTarget;
          setOpenPicker(openPicker === type ? null : type);
        }}
      >
        &#8943;
      </button>
      {openPicker === type && (
        <div className="armor-picker-menu" ref={popoverRef}>
          <strong>{t("equipment.choose", { name: label })}</strong>
          <button
            className={!equippedItem ? "selected" : ""}
            type="button"
            onClick={() => selectPrimaryArmor(type, "")}
          >
            {t("equipment.nothing")}
          </button>
          {slotItems.map((item) => (
            <button
              aria-label={t("equipment.equip", { name: getItemName(item) })}
              className={item.isActive ? "selected" : ""}
              key={item.id}
              type="button"
              onClick={() => selectPrimaryArmor(type, item.id)}
            >
              <span>{getItemName(item)}</span>
              <span>{formatModifier(item.armorClass)} AC</span>
            </button>
          ))}
          {slotItems.length === 0 && (
            <small>{t("equipment.noItems")}</small>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="armor-table">
      <h2>{t("equipment.armor")}</h2>
      <div className="armor-values">
        <label>
          <span>{t("equipment.baseAc")}</span>
          <input
            aria-label={t("equipment.baseAc")}
            type="number"
            value={characteristics.baseArmorClass}
            onChange={(event) =>
              updateNumber("baseArmorClass", event.target.value)
            }
          />
        </label>
        <label>
          <span>{t("equipment.additionalModifier")}</span>
          <input
            aria-label={t("equipment.additionalModifier")}
            type="number"
            value={characteristics.additionalArmorClassModifier}
            onChange={(event) =>
              updateNumber("additionalArmorClassModifier", event.target.value)
            }
          />
        </label>
      </div>

      <table className="armor-equipment-table">
        <thead>
          <tr>
            <th>{t("equipment.slot")}</th>
            <th>{t("equipment.name")}</th>
            <th>{t("equipment.ac")}</th>
            <th aria-label={t("equipment.actions")} />
          </tr>
        </thead>
        <tbody>
          {PRIMARY_ARMOR_SLOTS.map((type) => {
            const label = t(systemLabelKey("armorTypes", type));
            const slotItems = armorItems.filter(
              (item) => item.armorType === type
            );
            const equippedItem = slotItems.find((item) => item.isActive);

            return (
              <tr key={type}>
                <th className="armor-slot-name" scope="row">
                  {label}
                </th>
                <td className="active-item">
                  {equippedItem ? getItemName(equippedItem) : t("equipment.nothing")}
                </td>
                <td>
                  {equippedItem
                    ? formatModifier(equippedItem.armorClass)
                    : "\u2014"}
                </td>
                <td>
                  {renderPrimaryPicker(
                    type,
                    label,
                    slotItems,
                    equippedItem
                  )}
                </td>
              </tr>
            );
          })}

          <tr className="armor-type">
            <td className="section-name" colSpan={4}>
              {t("equipment.other")}
            </td>
          </tr>
          {equippedAccessories.map((item) => (
            <tr key={item.id}>
              <td />
              <td className="active-item">{getItemName(item)}</td>
              <td>{formatModifier(item.armorClass)}</td>
              <td>
                <button
                  aria-label={t("equipment.unequip", { name: getItemName(item) })}
                  className="armor-remove-button"
                  type="button"
                  onClick={() => toggleAccessory(item)}
                >
                  &times;
                </button>
              </td>
            </tr>
          ))}
          {equippedAccessories.length === 0 && (
            <tr>
              <td />
              <td className="active-item">{t("equipment.nothing")}</td>
              <td>&mdash;</td>
              <td />
            </tr>
          )}
          <tr>
            <td />
            <td className="armor-manage-label">{t("equipment.manageAccessories")}</td>
            <td />
            <td>
              <div className="armor-picker-wrapper">
                <button
                  aria-expanded={openPicker === "other"}
                  aria-label={t("equipment.choose", { name: t("equipment.other") })}
                  className="armor-picker-button"
                  type="button"
                  onClick={(event) => {
                    triggerRef.current = event.currentTarget;
                    setOpenPicker(openPicker === "other" ? null : "other")
                  }}
                >
                  &#8943;
                </button>
                {openPicker === "other" && (
                  <div className="armor-picker-menu armor-picker-menu-right" ref={popoverRef}>
                    <strong>{t("equipment.other")}</strong>
                    {accessories.map((item) => (
                      <button
                        aria-label={t(item.isActive ? "equipment.unequip" : "equipment.equip", { name: getItemName(item) })}
                        className={item.isActive ? "selected" : ""}
                        key={item.id}
                        type="button"
                        onClick={() => toggleAccessory(item)}
                      >
                        <span>{item.isActive ? "✓ " : ""}{getItemName(item)}</span>
                        <span>{formatModifier(item.armorClass)} AC</span>
                      </button>
                    ))}
                    {accessories.length === 0 && (
                      <small>{t("equipment.noItems")}</small>
                    )}
                    <button type="button" onClick={() => setOpenPicker(null)}>
                      {t("common.close")}
                    </button>
                  </div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Armor;
